<template>
    <section v-if="scoreQuestions.length > 0 || textQuestions.length > 0">
        <header class="flex items-end justify-between gap-4 mb-4">
            <div>
                <h2 class="title-section text-ink">{{ section.title }}</h2>
                <p v-if="section.description" class="body-small text-ink-muted mt-2">{{ section.description }}</p>
            </div>
            <div v-if="section.avg !== null" class="text-right shrink-0">
                <div class="flex items-baseline gap-2 justify-end">
                    <span class="details uppercase tracking-wide text-ink-muted">Avg</span>
                    <span class="title-body" :class="avgColorClass">{{ formatNumber(section.avg, "two-decimals") }}</span>
                </div>
                <div v-if="avgPctOfScale !== null" class="details text-ink-muted mt-1">{{ formatNumber(avgPctOfScale / 100, "percent") }} of scale</div>
            </div>
        </header>

        <div class="grid grid-cols-1 gap-10" :class="isTwoColumn ? 'lg:grid-cols-2' : ''">
            <!-- Left: per-question distribution, optionally grouped into subsection bands. -->
            <div v-if="scoreQuestions.length > 0">
                <div class="flex items-center justify-between gap-4 mb-4">
                    <div class="label-section">Per-skill distribution</div>
                </div>
                <DistributionLegend :max-score="legendMaxScore" :labels="legendLabels" class="mb-4" />
                <div v-if="questionGroups.length > 1" class="flex flex-col gap-10">
                    <div v-for="group in questionGroups" :key="group.key">
                        <header class="flex items-end justify-between gap-4 mb-3 pb-2 border-b border-border">
                            <h3 class="body-small-bold uppercase tracking-wider text-ink">{{ group.title }}</h3>
                            <div v-if="group.avg !== null" class="details text-ink-muted">
                                Avg <span class="font-semibold text-ink">{{ formatNumber(group.avg, "two-decimals") }}</span>
                            </div>
                        </header>
                        <div class="flex flex-col gap-5">
                            <div v-for="q in group.questions" :key="q.question_id">
                                <div class="flex items-start justify-between gap-3 mb-2">
                                    <div class="leading-snug">
                                        <div class="body-small-bold text-ink">{{ q.title || q.text }}</div>
                                        <div v-if="q.title && q.text" class="details text-ink-muted mt-1">{{ q.text }}</div>
                                    </div>
                                    <div class="flex items-center gap-2 shrink-0">
                                        <span class="body-small-bold text-valence-blue-600">{{ formatNumber(q.all_avg, "two-decimals") || "—" }}</span>
                                    </div>
                                </div>
                                <DistributionBar :distribution="q.distribution" :max-score="q.max_score" />
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="flex flex-col gap-5">
                    <div v-for="q in flatScoreQuestions" :key="q.question_id">
                        <div class="flex items-start justify-between gap-3 mb-2">
                            <div class="leading-snug">
                                <div class="body-small-bold text-ink">{{ q.title || q.text }}</div>
                                <div v-if="q.title && q.text" class="details text-ink-muted mt-1">{{ q.text }}</div>
                            </div>
                            <div class="flex items-center gap-2 shrink-0">
                                <span class="body-small-bold text-valence-blue-600">{{ formatNumber(q.all_avg, "two-decimals") || "—" }}</span>
                            </div>
                        </div>
                        <DistributionBar :distribution="q.distribution" :max-score="q.max_score" />
                    </div>
                </div>
            </div>

            <!-- Right: freeform insight + open-text themes -->
            <div v-if="hasRightColumn" class="flex flex-col gap-6">
                <div v-if="section.insight?.summary">
                    <div class="label-section mb-3">Section insight</div>
                    <p v-if="section.insight.headline" class="nadia-subtitle text-ink mb-4">
                        {{ section.insight.headline }}
                    </p>
                    <div
                        v-if="strengths.length > 0 || opportunities.length > 0"
                        class="grid gap-4 mb-4"
                        :class="strengths.length > 0 && opportunities.length > 0 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'">
                        <div v-if="strengths.length > 0" class="rounded-xl bg-surface-raised border border-border p-5">
                            <div class="flex items-center gap-2 mb-4">
                                <i class="bi bi-arrow-up-circle-fill text-ink" aria-hidden="true"></i>
                                <span class="details uppercase tracking-wide font-semibold text-ink">Strengths</span>
                            </div>
                            <ul class="flex flex-col">
                                <li v-for="(h, i) in strengths" :key="`s${i}`" class="py-3 first:pt-0 last:pb-0 border-t border-border first:border-t-0">
                                    <div class="nadia-chat-bold text-ink leading-snug">{{ h.label }}</div>
                                    <div class="body-small text-ink-muted leading-relaxed mt-2">{{ h.detail }}</div>
                                    <div v-if="h.related_question_title" class="mt-2 inline-flex items-center gap-1 details uppercase tracking-wide font-semibold text-ink">
                                        <i class="bi bi-link-45deg" aria-hidden="true"></i>
                                        {{ h.related_question_title }}
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <div v-if="opportunities.length > 0" class="rounded-xl bg-surface-raised border border-border p-5">
                            <div class="flex items-center gap-2 mb-4">
                                <i class="bi bi-arrow-down-circle-fill text-ink" aria-hidden="true"></i>
                                <span class="details uppercase tracking-wide font-semibold text-ink">Opportunities</span>
                            </div>
                            <ul class="flex flex-col">
                                <li v-for="(h, i) in opportunities" :key="`o${i}`" class="py-3 first:pt-0 last:pb-0 border-t border-border first:border-t-0">
                                    <div class="nadia-chat-bold text-ink leading-snug">{{ h.label }}</div>
                                    <div class="body-small text-ink-muted leading-relaxed mt-2">{{ h.detail }}</div>
                                    <div v-if="h.related_question_title" class="mt-2 inline-flex items-center gap-1 details uppercase tracking-wide font-semibold text-ink">
                                        <i class="bi bi-link-45deg" aria-hidden="true"></i>
                                        {{ h.related_question_title }}
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <p class="body-small text-ink-muted leading-relaxed border-l-2 border-border-strong pl-4">
                        {{ section.insight.summary }}
                    </p>
                </div>

                <div v-if="textQuestions.length > 0">
                    <div class="label-section mb-3">Open-text response</div>
                    <div class="grid gap-6" :class="!isTwoColumn && textQuestions.length > 1 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'">
                        <div v-for="q in textQuestions" :key="q.question_id">
                            <div class="body-small-bold text-ink leading-snug">&ldquo;{{ q.text }}&rdquo;</div>
                            <div class="details text-ink-muted mt-1">
                                {{ q.open_text_count ?? 0 }} responses<template v-if="(q.themes?.length ?? 0) > 0">
                                    · clustered into {{ q.themes!.length }} themes by Nadia</template
                                >
                            </div>
                            <div v-if="(q.themes?.length ?? 0) > 0" class="grid gap-3 mt-3" :class="themesInTwoCols ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'">
                                <div v-for="(theme, i) in q.themes" :key="i" class="rounded-lg border border-border bg-surface-raised p-4">
                                    <div class="text-ink body-small-bold">{{ theme.title }}</div>
                                    <p v-if="theme.summary" class="body-small text-ink-muted mt-2 leading-relaxed border-l-2 border-border-strong pl-3">
                                        {{ theme.summary }}
                                    </p>
                                </div>
                            </div>
                            <div v-else-if="(q.open_text_count ?? 0) > 0" class="details text-ink-muted mt-2">Not enough responses yet to cluster into themes.</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { formatNumber } from "~vue/utils/formatNumber";
import { computed } from "vue";

import DistributionBar from "./DistributionBar.vue";
import DistributionLegend from "./DistributionLegend.vue";
import type { PerQuestion, SectionAggregate } from "./types";

interface Props {
    section: SectionAggregate;
    questions: PerQuestion[];
    // Direct child sections of `section`, if any. When present, the per-skill
    // column renders one band per subsection (e.g. UPS Leadership Effectiveness →
    // Strategize / Inspire / Deliver). Each subsection still carries its own
    // rolled-up `avg`, so the band header can show a sub-average.
    subsections?: SectionAggregate[];
}

const props = withDefaults(defineProps<Props>(), {
    subsections: () => [],
});

const scoreQuestions = computed(() => props.questions.filter((q) => q.is_score && q.distribution.length > 0 && q.all_count > 0));
const textQuestions = computed(() => props.questions.filter((q) => q.is_text));
const hasRightColumn = computed(() => Boolean(props.section.insight?.summary) || textQuestions.value.length > 0);
const isTwoColumn = computed(() => scoreQuestions.value.length > 0 && hasRightColumn.value);
// Theme cards can wrap to two columns only when there's enough horizontal
// room — i.e., the panel is single-column AND the single text question is
// using the full width.
const themesInTwoCols = computed(() => !isTwoColumn.value && textQuestions.value.length === 1);

// Flat list preserves input order (which is the seed/template order from the
// backend). We deliberately don't sort by score here — for grouped sections the
// subsection narrative matters more than ranking, and for flat sections seed
// order matches how raters experienced the survey.
const flatScoreQuestions = computed(() => scoreQuestions.value.slice());

interface QuestionGroup {
    key: string;
    title: string;
    avg: number | null;
    questions: PerQuestion[];
}

// One group per subsection that has score questions; subsections with no
// questions are dropped. Falls back to a single empty list when no subsections
// were provided, which collapses to the flat rendering in the template.
const questionGroups = computed<QuestionGroup[]>(() => {
    if (props.subsections.length === 0) {
        return [];
    }
    const ordered = props.subsections.slice().sort((a, b) => a.order - b.order);
    const groups: QuestionGroup[] = [];
    for (const sub of ordered) {
        const qs = scoreQuestions.value.filter((q) => q.section_id === sub.section_id);
        if (qs.length === 0) {
            continue;
        }
        groups.push({ key: sub.section_id, title: sub.title ?? "", avg: sub.avg, questions: qs });
    }
    return groups;
});

const legendMaxScore = computed(() => scoreQuestions.value[0]?.max_score ?? scoreQuestions.value[0]?.distribution.length ?? 5);
const legendLabels = computed(() => scoreQuestions.value[0]?.scale_labels);

const strengths = computed(() => props.section.insight?.strengths ?? []);
const opportunities = computed(() => props.section.insight?.opportunities ?? []);

// Color the section average by where it sits on the scale. The scale max for
// a section is whatever its widest underlying question used; fall back to 5
// (Likert default) when older reports don't carry max_score on sections.
const avgPctOfScale = computed(() => {
    const avg = props.section.avg;
    const max = props.section.max_score ?? legendMaxScore.value ?? 5;
    if (avg === null || avg === undefined || !max) {
        return null;
    }
    return Math.round((avg / max) * 100);
});

const avgColorClass = computed(() => {
    const pct = avgPctOfScale.value;
    if (pct === null) {
        return "text-ink";
    }
    if (pct >= 75) {
        return "text-ink";
    }
    if (pct >= 50) {
        return "text-ink";
    }
    return "text-chip-status-overdue-text";
});
</script>
